import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import ReactPlayer, { FilePlayerProps } from 'react-player/file';
import cm from 'classnames';

import { EFFECT_VALUE_CSS_OFFSET } from 'constants/params';
import { FileConfig, Rotate } from 'services/file.service';
import { useLocation, Path } from 'components/_hooks/use-router';
import { useAppSelector } from 'store/store-hooks';

import { useCssFilter } from 'components/app/_mediator/css-filter/use-css-filter';
import { useCssFilterClip } from 'components/app/_mediator/css-filter/use-css-filter-clip';
import { useCssRotate } from 'components/app/_mediator/css-rotate/use-css-rotate';
import { Clip } from 'components/app/editor/_mediator/editor.slice';

import { CustomControls } from './_player-controls';
import styles from './_enhanced-video-player.module.scss';

export interface OnProgress {
  played: Fraction;
  loaded: Fraction;
  playedSeconds: Seconds;
  loadedSeconds: Seconds;
}

export interface EnhancedVideoPlayerProps extends FilePlayerProps {
  // Video content props
  url?: string;
  playing?: boolean;
  volume?: number;
  muted?: boolean;

  // Transformation props
  fileConfig?: FileConfig;
  clipData?: Clip;
  rotate?: number | null;
  flip?: boolean;
  brightness?: number;
  contrast?: number;

  // Control props
  showCustomControls?: boolean;
  controls?: boolean;
  isTsSliderHovered?: boolean;
  isHighlightSensitivityActive?: boolean;

  // Styling props
  className?: string;
  videoClassName?: string;
  controlsClassName?: string;
}

// Add imperative handle interface
export interface EnhancedVideoPlayerRef {
  seekTo: (time: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  play: () => void;
  pause: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getInternalPlayer: () => any;
}

export const Player = React.memo(
  React.forwardRef<EnhancedVideoPlayerRef, EnhancedVideoPlayerProps>((props, ref) => {
    const {
      url,
      playing,
      volume = 1,
      muted = false,
      fileConfig,
      clipData,
      rotate,
      flip,
      showCustomControls = false,
      className,
      videoClassName,
      controlsClassName,
      config,
      progressInterval = 1000,
      onVideoReady,
      onTimeUpdate,
      onProgress,
      onSeek,
      onBuffer,
      onBufferEnd,
      onEnded,
      onPlay,
      onPause,
      isTsSliderHovered = false,
      isHighlightSensitivityActive = false,
    } = props;

    const videoContainerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<ReactPlayer>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(muted);
    const [currentVolume, setCurrentVolume] = useState(volume);
    const [isSeeking, setIsSeeking] = useState(false);
    const [pendingSeek, setPendingSeek] = useState<number | null>(null);
    const [showControls, setShowControls] = useState(true);
    const [isMouseOver, setIsMouseOver] = useState(false);

    // Get intro state to detect when we're on the srSeek step
    const intro = useAppSelector((state) => state.app.intro);
    const { path } = useLocation();

    // Automatically show controls when the srSeek element is highlighted by the intro tour
    useEffect(() => {
      // Check if we're on the intro tour and on the selector page (where srSeek step happens)
      if (intro === true && path === Path.selector) {
        setShowControls(true);
      }
    }, [intro, path]);

    const modifiedFileConfig = useMemo(() => {
      if (fileConfig) {
        return {
          ...fileConfig,
          clipData,
          rotate: rotate !== null && rotate !== undefined ? (rotate as Rotate) : null,
          flip,
        };
      }
      return undefined;
    }, [fileConfig, clipData, rotate, flip]);

    // Use useCssFilterClip for real-time brightness/contrast updates
    const brightness = useCssFilterClip('brightness', clipData, modifiedFileConfig);
    const contrast = useCssFilterClip('contrast', clipData, modifiedFileConfig);

    const cssFilter = useMemo(() => {
      const filter = `brightness(${brightness + EFFECT_VALUE_CSS_OFFSET}%) contrast(${
        contrast + EFFECT_VALUE_CSS_OFFSET
      }%)`;
      return filter;
    }, [brightness, contrast]);

    const cssFilterRef = useCssFilter({ brightness, contrast });
    const rotateData = useCssRotate(cssFilterRef, {
      fileConfig: modifiedFileConfig,
      clipData,
      rotate,
      flip,
    });

    // Control handlers
    const handlePlayPause = useCallback(() => {
      if (isPlaying) {
        playerRef.current?.getInternalPlayer()?.pause();
      } else {
        playerRef.current?.getInternalPlayer()?.play();
      }
    }, [isPlaying]);

    const handleVolumeChange = useCallback((newVolume: number) => {
      setCurrentVolume(newVolume);
      if (playerRef.current?.getInternalPlayer()) {
        (playerRef.current.getInternalPlayer() as HTMLVideoElement).volume = newVolume;
      }
    }, []);

    const handleMuteToggle = useCallback(() => {
      const newMuted = !isMuted;
      setIsMuted(newMuted);
      if (playerRef.current?.getInternalPlayer()) {
        (playerRef.current.getInternalPlayer() as HTMLVideoElement).muted = newMuted;
      }
    }, [isMuted]);

    const handleSeek = useCallback((time: number) => {
      setCurrentTime(time); // Update currentTime immediately for instant slider movement
      playerRef.current?.seekTo(time, 'seconds');

      // Show controls on every seek for testing UX
      setShowControls(true);
    }, []);

    // Expose imperative handle methods
    const imperativeHandle = useMemo(
      () => ({
        seekTo: handleSeek,
        getCurrentTime: () => currentTime,
        getDuration: () => duration,
        play: () => playerRef.current?.getInternalPlayer()?.play(),
        pause: () => playerRef.current?.getInternalPlayer()?.pause(),
        getInternalPlayer: () => playerRef.current?.getInternalPlayer(),
      }),
      [handleSeek, currentTime, duration]
    );

    // Expose imperative handle through ref
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(imperativeHandle);
        } else {
          ref.current = imperativeHandle;
        }
      }
    }, [ref, imperativeHandle]);

    const handlePlayerReady = useCallback(() => {
      if (onVideoReady) {
        onVideoReady(playerRef.current!);
      }
    }, [onVideoReady]);

    useEffect(() => {
      if (fileConfig) {
        const player = playerRef.current?.getInternalPlayer();
        if (player) {
          player.volume = currentVolume;
        }
      }
    }, [fileConfig, currentVolume]);

    useEffect(() => {
      setIsPlaying(playing ?? false);
    }, [playing]);

    // Show controls when TsSlider is hovered or Highlight Sensitivity is active
    useEffect(() => {
      if (isTsSliderHovered || isHighlightSensitivityActive) {
        setShowControls(true);
      } else if (!isMouseOver) {
        setShowControls(false);
      }
    }, [isTsSliderHovered, isHighlightSensitivityActive, isMouseOver]);

    const handleMouseEnter = useCallback(() => {
      setIsMouseOver(true);
      setShowControls(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsMouseOver(false);
      // Only hide controls if TsSlider is not hovered and Highlight Sensitivity is not active
      if (!isTsSliderHovered && !isHighlightSensitivityActive) {
        setShowControls(false);
      }
    }, [isTsSliderHovered, isHighlightSensitivityActive]);

    return (
      <div
        className={cm(styles.enhancedVideoPlayer, className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={videoContainerRef} className={cm(styles.videoWrapper, videoClassName)}>
          <div ref={cssFilterRef} className={styles.videoContent}>
            <ReactPlayer
              ref={playerRef}
              url={url}
              playing={isPlaying}
              volume={currentVolume}
              muted={isMuted}
              config={config}
              progressInterval={progressInterval}
              onReady={handlePlayerReady}
              onProgress={(state: OnProgress) => {
                if (pendingSeek !== null && Math.abs(state.playedSeconds - pendingSeek) < 0.05) {
                  setPendingSeek(null);
                }
                if (!isSeeking) {
                  setCurrentTime(state.playedSeconds);
                }
                if (onTimeUpdate) {
                  onTimeUpdate(state.playedSeconds, duration);
                }
                // Forward the onProgress callback if provided
                if (onProgress) {
                  onProgress(state);
                }
              }}
              onDuration={setDuration}
              onSeek={onSeek}
              onBuffer={onBuffer}
              onBufferEnd={onBufferEnd}
              onEnded={onEnded}
              onPlay={() => {
                setIsPlaying(true);
                // Hide controls on play if mouse is not over the player
                if (!isMouseOver) {
                  setShowControls(false);
                }
                // Call the passed onPlay callback if provided
                if (onPlay) {
                  onPlay();
                }
              }}
              onPause={() => {
                setIsPlaying(false);
                setShowControls(true);
                // Call the passed onPause callback if provided
                if (onPause) {
                  onPause();
                }
              }}
              onSeeked={() => {
                setIsSeeking(false);
              }}
            />
          </div>
        </div>

        {showCustomControls && (
          <div className={cm(styles.controlsOverlay, controlsClassName, { [styles.hidden]: !showControls })}>
            <CustomControls
              playerRef={playerRef}
              videoContainerRef={videoContainerRef}
              cssFilterRef={cssFilterRef}
              cssFilter={cssFilter}
              currentTime={currentTime}
              duration={duration}
              isPlaying={isPlaying}
              isMuted={isMuted}
              currentVolume={currentVolume}
              onPlayPause={handlePlayPause}
              onVolumeChange={handleVolumeChange}
              onMuteToggle={handleMuteToggle}
              onSeek={handleSeek}
              rotateData={rotateData}
              brightness={brightness}
              contrast={contrast}
              isSeeking={isSeeking}
              setIsSeeking={setIsSeeking}
              pendingSeek={pendingSeek}
              setPendingSeek={setPendingSeek}
            />
          </div>
        )}
      </div>
    );
  })
);

Player.displayName = 'Player';
